import { keys } from '@core/Services/Analytics/keys'
import { trackEvent, trackKey } from '@core/Services/Analytics/track'
import type { useAddToBlackbook } from './Hooks/useAddToBlackbook'

export const trackBlackbookEntryToggled = (isExpanded: boolean) => {
  trackEvent('blackbook-entry-expanded', { isExpanded })
}

export const trackBlackbookNavigateToRace = () => {
  trackKey('blackbook-navigate-to-race')
}
export const trackBlackbookOddsClicked = (
  data: Omit<BlackbookControlAnalyticsData, 'source'> & { action: 'betslip' | 'quickbet' }
) => {
  const { code, type } = getCodeAndType(data)

  trackEvent('blackbook-odds-clicked', {
    name: data.name,
    action: data.action,
    raceCode: typeof code === 'string' ? code.toLowerCase() : undefined,
    type,
  })
}

export const trackBlackbookEntryDeleteClicked = () => {
  trackKey('blackbook-entry-delete-clicked')
}
export const trackBlackbookEntryDeleted = (type: string) => {
  trackEvent('blackbook-entry-deleted', { type })
}
export const trackBlackbookEntryDeleteCanceled = () => {
  trackKey('blackbook-entry-delete-canceled')
}
export const trackBlackbookSortChanged = (sortBy: string) => {
  trackEvent('blackbook-sort-changed', { sortBy })
}
export const trackBlackbookAddCommentClicked = () => {
  trackKey('blackbook-comment-clicked')
}
export const trackBlackbookAddCommentCancel = () => {
  trackKey('blackbook-comment-cancel')
}
export const trackBlackbookAddCommentSave = (
  data: Omit<BlackbookControlAnalyticsData, 'source'>
) => {
  const { code, type } = getCodeAndType(data)

  trackEvent('blackbook-comment-save', {
    name: data.name,
    raceCode: typeof code === 'string' ? code.toLowerCase() : undefined,
    type,
  })
}

export const trackBlackbookChangeView = (type: string) => {
  trackEvent('blackbook-change-view', { type })
}

export const trackBlackbookUnableToAdd = (
  data: BlackbookControlAnalyticsData & { reason: 'not-matched' | 'limit-exceeded' | 'other' }
) =>
  trackEvent('blackbook-unable-to-add', {
    source: data.source,
    name: data.name,
    reason: data.reason,
    raceCode: typeof data.code === 'string' ? data.code.toLowerCase() : undefined,
  })

export const trackBlackbookAddRunner = (data: BlackbookControlAnalyticsData) => {
  const { code, type } = getCodeAndType(data)
  trackEvent('blackbook-add-runner', {
    name: data.name,
    source: data.source,
    raceCode: typeof code === 'string' ? code.toLowerCase() : undefined,
    type,
  })
}

export const trackBlackbookGoToBlackbook = (data: BlackbookControlAnalyticsData) => {
  const { code, type } = getCodeAndType(data)

  trackEvent('blackbook-go-to-blackbook', {
    name: data.name,
    source: data.source,
    raceCode: typeof code === 'string' ? code.toLowerCase() : undefined,
    type,
  })
}

export const trackBlackbookAlertEvent = (
  eventType: 'push' | 'sms' | 'closed',
  data: Record<string, unknown> = {}
): void => {
  let eventKey:
    | typeof keys.blackbookPushAlertChosen
    | typeof keys.blackbookSmsAlertChosen
    | typeof keys.blackbookAlertPromptClosed

  switch (eventType) {
    case 'push':
      eventKey = keys.blackbookPushAlertChosen
      break
    case 'sms':
      eventKey = keys.blackbookSmsAlertChosen
      break
    default:
      eventKey = keys.blackbookAlertPromptClosed
      break
  }
  trackEvent(eventKey, { ...data })
}

// =============
// Local Helpers
// =============

function getCodeAndType(data: Omit<BlackbookControlAnalyticsData, 'source'>): {
  code: BlackbookControlAnalyticsData['code']
  type: BlackbookEntryType
} {
  const code: BlackbookControlAnalyticsData['code'] = data.code === 'Jockeys' ? 'Races' : data.code
  const type = data.code === 'Jockeys' ? 'jockey' : 'runner'
  return { code, type }
}

// =====
// Types
// =====

type BlackbookEntryType = 'runner' | 'jockey'

type BlackbookControlAnalyticsData = {
  name: string
  code: Parameters<typeof useAddToBlackbook>[0]['starterType']
} & Pick<Parameters<typeof useAddToBlackbook>[0], 'source'>
